// src/components/foam/FoamLanding.js
//
// Screen 1 · QR Landing + Sign-in (Foam direction)
// Doc: 2026-06 design-system handoff "Screen 1" + SPEC §2.1
// Updated 2026-06-04: WAKA Design System pass — Saira Condensed headline,
// pinned-footer layout, square corners, logo PNG.
//
// Two contexts:
//   - `/r/:sku` — customer arrived via QR scan; SKU is known, product card shown
//   - `/`       — no SKU yet; serial input + "where do I find it" drawer shown

import React, { useState } from 'react';
import { FScreen, FHeader, FLabel, FBrandText, FHeadline, FBoardCard, FBtn, FSignInButtons } from './FoamPrimitives';
import FoamSerialHelp from './FoamSerialHelp';

const FoamLanding = ({
  product,           // { name, image, caption } if SKU is known, else null
  onSignInApple,
  onSignInGoogle,
  onSerialSubmit,    // (sku) => void; only used when no `:sku` in URL
  authError,
  signingIn = false,
}) => {
  const [serialInput, setSerialInput] = useState('');
  // Root-screen sign-in for returning owners (Nigel, 2026-06-05) — collapsed
  // behind a link so the register funnel stays clean.
  const [showSignIn, setShowSignIn] = useState(false);
  const hasProduct = !!product;

  const footer = hasProduct ? (
    <>
      {authError && (
        <div className="font-mono text-[12px] tracking-[0.1em] uppercase text-foam-orange-d">
          ● {authError}
        </div>
      )}
      <FSignInButtons
        onApple={onSignInApple}
        onGoogle={onSignInGoogle}
        disabled={signingIn}
      />
      <FooterNote />
    </>
  ) : (
    <>
      <FBtn
        primary
        type="submit"
        disabled={!serialInput.trim()}
        onClick={() => {
          if (serialInput.trim()) onSerialSubmit?.(serialInput.trim().toUpperCase());
        }}
      >
        Continue →
      </FBtn>

      {showSignIn ? (
        <>
          {authError && (
            <div className="font-mono text-[12px] tracking-[0.1em] uppercase text-foam-orange-d">
              ● {authError}
            </div>
          )}
          <FSignInButtons
            onApple={onSignInApple}
            onGoogle={onSignInGoogle}
            disabled={signingIn}
          />
        </>
      ) : (
        <button
          type="button"
          onClick={() => setShowSignIn(true)}
          className="font-mono text-[12px] tracking-[0.08em] uppercase underline text-foam-ink-2 text-center w-full py-[4px]"
          style={{ background: 'transparent' }}
        >
          Already registered? Sign in
        </button>
      )}

      <FooterNote />
    </>
  );

  return (
    <FScreen header={<FHeader right={<FLabel>Register</FLabel>} />} footer={footer}>
      <div className="mt-[6px]">
        <FLabel color="var(--foam-orange)">● You just scanned a board</FLabel>
        <div className="mt-[14px]">
          <FHeadline size={60} lineHeight={0.9}>
            Let's make<br />
            this <FHeadline.Accent>yours.</FHeadline.Accent>
          </FHeadline>
        </div>
        <p className="text-[14px] leading-[1.5] text-foam-ink-2 mt-[16px]">
          {hasProduct
            ? "If you just bought this WAKA, you're in the right place. Sign in once and it's registered to your name."
            : 'Enter the serial number from your board to get started.'}
        </p>
      </div>

      {hasProduct ? (
        <FBoardCard product={product} />
      ) : (
        <div className="flex flex-col gap-[18px]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (serialInput.trim()) onSerialSubmit?.(serialInput.trim().toUpperCase());
            }}
          >
            <input
              type="text"
              value={serialInput}
              onChange={(e) => setSerialInput(e.target.value)}
              placeholder="GLIDE-88-V2-A0427"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
              className="
                w-full font-mono text-[16px] tracking-[0.05em]
                h-[60px] px-[18px]
                bg-foam-paper-2 text-foam-ink
                placeholder:text-foam-mute
                focus:outline-none focus:ring-2 focus:ring-foam-ink
                border-0
              "
              style={{ borderRadius: 0 }}
            />
          </form>
          <FoamSerialHelp />
        </div>
      )}
    </FScreen>
  );
};

const FooterNote = () => (
  <div className="text-[12px] text-foam-ink-2 text-center leading-[1.5] mt-[4px]">
    Just browsing? Head to{' '}
    <a href="https://waka.surf" className="text-foam-ink no-underline">
      {/* url lockups stay lowercase per the design system (Nigel, 2026-06-05) */}
      <FBrandText size={11} color="var(--foam-ink)" caps={false} style={{ letterSpacing: '0.05em' }}>
        waka.surf
      </FBrandText>
    </a>{' '}
    instead.
  </div>
);

export default FoamLanding;
